import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { deleteData, getData, getRemoteUrl, patchData, postData } from './request'
import { getLoginToken } from './store'
import { getCurrentSessionGeneration, sessionInvalidated } from './session'
import { todayLocal } from '../plannerLogic'

let requestSeq = 0

const RANGE_CACHE_LIMIT = 24
const ACTIVITY_RANGE_CACHE_LIMIT = 24

export function nextRequestId() {
    requestSeq += 1
    return requestSeq
}

// LRU recency list of cached range keys, most recently requested first. The
// planner caches visited months so switching back is instant; the cap bounds
// memory for a long browsing session.
function bumpRangeOrder(order, key, limit) {
    return [key, ...(order || []).filter(k => k !== key)].slice(0, limit)
}

export function planRangeKey(dateFrom, dateTo) {
    return `${dateFrom}:${dateTo}`
}

export function activityRangeKey(startFrom, startBefore) {
    return `${startFrom}:${startBefore}`
}

function authGet(url) {
    return getData(url, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${getLoginToken()}` },
    })
}

function dateWithin(rowDate, dateFrom, dateTo) {
    return rowDate >= dateFrom && rowDate <= dateTo
}

function comparePlans(a, b) {
    if (a.date < b.date) return -1
    if (a.date > b.date) return 1
    return a.id - b.id
}

function makePlanRange(range) {
    return {
        dateFrom: range.dateFrom,
        dateTo: range.dateTo,
        rows: [],
        status: 'idle',
        error: null,
        requestId: 0,
        sessionGeneration: 0,
        mutationVersion: 0,
        stale: false,
    }
}

function makeActivityRange(range) {
    return {
        startFrom: range.startFrom,
        startBefore: range.startBefore,
        rows: [],
        status: 'idle',
        error: null,
        requestId: 0,
        sessionGeneration: 0,
        mutationVersion: 0,
        stale: false,
    }
}

function initialState() {
    return {
        ranges: {},
        activityRanges: {},
        rangeOrder: [],
        activityRangeOrder: [],
        activePlanRange: null,
        activeActivityRange: null,
        mutationVersion: 0,
        sessionGeneration: 0,
        createStatus: 'idle',
        createError: null,
        planMutations: {},
    }
}

function planRangeRequest(range, requestId) {
    return {
        ...range,
        requestId,
    }
}

export const asyncReqPlans = createAsyncThunk(
    'plans/request',
    async ({ dateFrom, dateTo, requestId }, { getState, rejectWithValue }) => {
        const sessionGeneration = getCurrentSessionGeneration()
        const mutationVersion = getState().plans.mutationVersion
        const key = planRangeKey(dateFrom, dateTo)
        const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo })
        try {
            const rows = await authGet(getRemoteUrl(`workout-plans?${params.toString()}`))
            return { key, requestId, sessionGeneration, mutationVersion, rows: rows || [] }
        } catch (error) {
            return rejectWithValue({ key, requestId, sessionGeneration, mutationVersion, error })
        }
    }
)

export const asyncReqPlannerActivities = createAsyncThunk(
    'plans/requestActivities',
    async ({ startFrom, startBefore, requestId }, { getState, rejectWithValue }) => {
        const sessionGeneration = getCurrentSessionGeneration()
        const mutationVersion = getState().plans.mutationVersion
        const key = activityRangeKey(startFrom, startBefore)
        const params = new URLSearchParams({ start_from: String(startFrom), start_before: String(startBefore) })
        try {
            const rows = await authGet(getRemoteUrl(`activities?${params.toString()}`))
            return { key, requestId, sessionGeneration, mutationVersion, rows: rows || [] }
        } catch (error) {
            return rejectWithValue({ key, requestId, sessionGeneration, mutationVersion, error })
        }
    }
)

export function startPlanRangeRequest(range) {
    return asyncReqPlans(planRangeRequest(range, nextRequestId()))
}

export function startPlannerActivitiesRequest(range) {
    return asyncReqPlannerActivities(planRangeRequest(range, nextRequestId()))
}

function applyMutationToRanges(state, payload) {
    const version = state.mutationVersion
    for (const key of Object.keys(state.ranges)) {
        const range = state.ranges[key]
        let changed = false

        if (payload.type === 'create') {
            const added = (payload.rows || []).filter(r =>
                dateWithin(r.date, range.dateFrom, range.dateTo))
            if (added.length) {
                const byId = new Map(range.rows.map(r => [r.id, r]))
                for (const row of added) byId.set(row.id, row)
                range.rows = Array.from(byId.values()).sort(comparePlans)
                changed = true
            }
        } else if (payload.type === 'move') {
            const row = payload.row
            const oldDate = payload.oldDate
            if (dateWithin(oldDate, range.dateFrom, range.dateTo)) {
                range.rows = range.rows.filter(r => r.id !== row.id)
                changed = true
            }
            if (dateWithin(row.date, range.dateFrom, range.dateTo)) {
                range.rows = [...range.rows.filter(r => r.id !== row.id), row].sort(comparePlans)
                changed = true
            }
        } else if (payload.type === 'delete') {
            const before = range.rows.length
            range.rows = range.rows.filter(r => r.id !== payload.deletedId)
            changed = range.rows.length !== before
        }

        if (changed) {
            range.stale = true
            range.mutationVersion = version
        }
    }
}

export const asyncCreatePlans = createAsyncThunk(
    'plans/create',
    async (items, { getState, dispatch, rejectWithValue }) => {
        const sessionGeneration = getCurrentSessionGeneration()
        try {
            const rows = await postData(getRemoteUrl('workout-plans'), { items }, getLoginToken())
            dispatch(plansSlice.actions.mutationApplied({
                type: 'create',
                rows: rows || [],
                sessionGeneration,
            }))
            const active = getState().plans.activePlanRange
            if (active) dispatch(startPlanRangeRequest(active))
            return rows || []
        } catch (error) {
            return rejectWithValue({ sessionGeneration, error })
        }
    }
)

export const asyncMovePlan = createAsyncThunk(
    'plans/move',
    async ({ id, date }, { getState, dispatch, rejectWithValue }) => {
        const sessionGeneration = getCurrentSessionGeneration()
        try {
            let oldDate = null
            for (const range of Object.values(getState().plans.ranges)) {
                const found = (range.rows || []).find(r => r.id === id)
                if (found) {
                    oldDate = found.date
                    break
                }
            }
            const row = await patchData(getRemoteUrl(`workout-plans/${id}`), { date }, getLoginToken())
            dispatch(plansSlice.actions.mutationApplied({
                type: 'move',
                row,
                oldDate,
                sessionGeneration,
            }))
            const active = getState().plans.activePlanRange
            if (active) dispatch(startPlanRangeRequest(active))
            return row
        } catch (error) {
            return rejectWithValue({ sessionGeneration, error })
        }
    }
)

export const asyncDeletePlan = createAsyncThunk(
    'plans/delete',
    async (id, { getState, dispatch, rejectWithValue }) => {
        const sessionGeneration = getCurrentSessionGeneration()
        try {
            await deleteData(getRemoteUrl(`workout-plans/${id}`), null, getLoginToken())
            dispatch(plansSlice.actions.mutationApplied({
                type: 'delete',
                deletedId: id,
                sessionGeneration,
            }))
            const active = getState().plans.activePlanRange
            if (active) dispatch(startPlanRangeRequest(active))
            return id
        } catch (error) {
            return rejectWithValue({ sessionGeneration, error })
        }
    }
)

function setPlanMutationPending(state, planId) {
    state.planMutations[planId] = { status: 'pending', error: null }
}

function setPlanMutationDone(state, planId) {
    state.planMutations[planId] = { status: 'idle', error: null }
}

function setPlanMutationError(state, planId, error) {
    state.planMutations[planId] = { status: 'error', error }
}

export const plansSlice = createSlice({
    name: 'plans',
    initialState: initialState(),
    reducers: {
        setActivePlanRange(state, { payload }) {
            state.activePlanRange = payload
            if (!payload) return
            const activeKey = planRangeKey(payload.dateFrom, payload.dateTo)
            const order = bumpRangeOrder(state.rangeOrder, activeKey, RANGE_CACHE_LIMIT)
            const todayKey = planRangeKey(todayLocal(), todayLocal())
            if (!order.includes(todayKey)) order.push(todayKey)
            state.rangeOrder = order
            for (const key of Object.keys(state.ranges)) {
                if (!order.includes(key)) delete state.ranges[key]
            }
        },
        setActiveActivityRange(state, { payload }) {
            state.activeActivityRange = payload
            if (!payload) return
            const activeKey = activityRangeKey(payload.startFrom, payload.startBefore)
            const order = bumpRangeOrder(state.activityRangeOrder, activeKey, ACTIVITY_RANGE_CACHE_LIMIT)
            state.activityRangeOrder = order
            for (const key of Object.keys(state.activityRanges)) {
                if (!order.includes(key)) delete state.activityRanges[key]
            }
        },
        mutationApplied(state, { payload }) {
            state.mutationVersion += 1
            applyMutationToRanges(state, payload)
        },
        plannerActivitiesChanged(state) {
            state.mutationVersion += 1
            for (const key of Object.keys(state.activityRanges)) {
                const range = state.activityRanges[key]
                if (range.status === 'ready') range.stale = true
            }
        },
        clearPlanMutation(state, { payload }) {
            state.planMutations[payload] = { status: 'idle', error: null }
        },
        resetCreateStatus(state) {
            state.createStatus = 'idle'
            state.createError = null
        },
    },
    extraReducers: builder => {
        builder.addCase(sessionInvalidated, (state, action) => ({
            ...initialState(),
            sessionGeneration: action.payload,
        }))

        builder.addCase(asyncReqPlans.pending, (state, action) => {
            const { dateFrom, dateTo, requestId } = action.meta.arg
            const key = planRangeKey(dateFrom, dateTo)
            const range = state.ranges[key] || makePlanRange({ dateFrom, dateTo })
            range.requestId = requestId
            range.mutationVersion = state.mutationVersion
            range.status = 'loading'
            range.error = null
            state.ranges[key] = range
            state.rangeOrder = bumpRangeOrder(state.rangeOrder, key, RANGE_CACHE_LIMIT)
        })
        builder.addCase(asyncReqPlans.fulfilled, (state, action) => {
            const payload = action.payload
            const range = state.ranges[payload.key]
            if (!range) return
            if (payload.requestId !== range.requestId) return
            if (payload.sessionGeneration !== state.sessionGeneration) return
            if (payload.mutationVersion !== state.mutationVersion) return
            range.rows = payload.rows
            range.status = 'ready'
            range.stale = false
            range.error = null
        })
        builder.addCase(asyncReqPlans.rejected, (state, action) => {
            const payload = action.payload
            if (!payload) return
            const range = state.ranges[payload.key]
            if (!range) return
            if (payload.requestId !== range.requestId) return
            if (payload.sessionGeneration !== state.sessionGeneration) return
            if (payload.mutationVersion !== state.mutationVersion) return
            range.stale = range.rows.length > 0
            range.status = range.rows.length ? 'ready' : 'error'
            range.error = payload.error
        })

        builder.addCase(asyncReqPlannerActivities.pending, (state, action) => {
            const { startFrom, startBefore, requestId } = action.meta.arg
            const key = activityRangeKey(startFrom, startBefore)
            const range = state.activityRanges[key] || makeActivityRange({ startFrom, startBefore })
            range.requestId = requestId
            range.mutationVersion = state.mutationVersion
            range.status = 'loading'
            range.error = null
            state.activityRanges[key] = range
            state.activityRangeOrder = bumpRangeOrder(state.activityRangeOrder, key, ACTIVITY_RANGE_CACHE_LIMIT)
        })
        builder.addCase(asyncReqPlannerActivities.fulfilled, (state, action) => {
            const payload = action.payload
            const range = state.activityRanges[payload.key]
            if (!range) return
            if (payload.requestId !== range.requestId) return
            if (payload.sessionGeneration !== state.sessionGeneration) return
            if (payload.mutationVersion !== state.mutationVersion) return
            range.rows = payload.rows
            range.status = 'ready'
            range.stale = false
            range.error = null
        })
        builder.addCase(asyncReqPlannerActivities.rejected, (state, action) => {
            const payload = action.payload
            if (!payload) return
            const range = state.activityRanges[payload.key]
            if (!range) return
            if (payload.requestId !== range.requestId) return
            if (payload.sessionGeneration !== state.sessionGeneration) return
            if (payload.mutationVersion !== state.mutationVersion) return
            range.stale = range.rows.length > 0
            range.status = range.rows.length ? 'ready' : 'error'
            range.error = payload.error
        })

        builder.addCase(asyncCreatePlans.pending, state => {
            state.createStatus = 'pending'
            state.createError = null
        })
        builder.addCase(asyncCreatePlans.fulfilled, state => {
            state.createStatus = 'idle'
            state.createError = null
        })
        builder.addCase(asyncCreatePlans.rejected, (state, action) => {
            state.createStatus = 'idle'
            const payload = action.payload
            if (!payload || payload.sessionGeneration === state.sessionGeneration) {
                state.createError = (payload && payload.error) || action.error || null
            }
        })

        builder.addCase(asyncMovePlan.pending, (state, action) => {
            setPlanMutationPending(state, action.meta.arg.id)
        })
        builder.addCase(asyncMovePlan.fulfilled, (state, action) => {
            setPlanMutationDone(state, action.meta.arg.id)
        })
        builder.addCase(asyncMovePlan.rejected, (state, action) => {
            const payload = action.payload
            const error = (payload && payload.error) || action.error || null
            setPlanMutationError(state, action.meta.arg.id, error)
        })

        builder.addCase(asyncDeletePlan.pending, (state, action) => {
            setPlanMutationPending(state, action.meta.arg)
        })
        builder.addCase(asyncDeletePlan.fulfilled, (state, action) => {
            setPlanMutationDone(state, action.meta.arg)
        })
        builder.addCase(asyncDeletePlan.rejected, (state, action) => {
            const payload = action.payload
            const error = (payload && payload.error) || action.error || null
            setPlanMutationError(state, action.meta.arg, error)
        })
    },
})

export const {
    setActivePlanRange,
    setActiveActivityRange,
    plannerActivitiesChanged,
    clearPlanMutation,
    resetCreateStatus,
} = plansSlice.actions

export function isCreatePlansInFlight(state) {
    return state.plans.createStatus === 'pending'
}

export function selectPlanRange(state, rangeKey) {
    return state.plans.ranges[rangeKey]
}

export function selectActivityRange(state, rangeKey) {
    return state.plans.activityRanges[rangeKey]
}

export function selectPlanMutation(state, planId) {
    return state.plans.planMutations[planId]
}